import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BIBLE_BOOKS } from "../../../mocks/bibleBooks";
import { useAuth } from "../../../hooks/useAuth";
import { useHighlights } from "../../../hooks/useHighlights";
import { ReaderTheme } from "../../../hooks/useReaderSettings";

interface Verse {
  pk: number;
  verse: number;
  text: string;
}

interface VerseDisplayProps {
  translation: string;
  bookId: number;
  chapter: number;
  theme: ReaderTheme;
  accentColor: string;
}

function cleanVerseText(raw: string): string {
  return raw
    .replace(/<S>\d+<\/S>/gi, "")
    .replace(/<e>.*?<\/e>/gi, "")
    .replace(/<n>.*?<\/n>/gi, "")
    .replace(/<del>.*?<\/del>/gi, "")
    .replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,;:.!?])/g, "$1")
    .trim();
}

interface LoginNudgeProps {
  onClose: () => void;
  isDark: boolean;
}
function LoginNudge({ onClose, isDark }: LoginNudgeProps) {
  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-xl border text-sm ${
        isDark
          ? "bg-[#111] border-[#2e2e2e] text-white"
          : "bg-white border-[#ddd] text-[#1a1a1a]"
      }`}
    >
      <i className="ri-bookmark-line" style={{ color: "#888" }} />
      <span>
        <a href="/login" className="font-medium underline" style={{ color: "#888" }}>
          Log in
        </a>{" "}
        to highlight &amp; save favorites
      </span>
      <button onClick={onClose} className="ml-1 cursor-pointer">
        <i className="ri-close-line text-[#555]" />
      </button>
    </div>
  );
}

export default function VerseDisplay({
  translation,
  bookId,
  chapter,
  theme,
  accentColor,
}: VerseDisplayProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [verses, setVerses] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hoveredVerse, setHoveredVerse] = useState<number | null>(null);
  const [showLoginNudge, setShowLoginNudge] = useState(false);
  const [savingVerse, setSavingVerse] = useState<number | null>(null);

  const book = BIBLE_BOOKS.find((b) => b.id === bookId);
  const isDark = theme === "dark";

  const { isHighlighted, getHighlightColor, toggleHighlight } = useHighlights(bookId, chapter);

  useEffect(() => {
    if (!translation || !bookId || !chapter) return;

    const cacheKey = `bible_v2_${translation}_${bookId}_${chapter}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        setVerses(JSON.parse(cached));
        return;
      } catch {
        // ignore
      }
    }

    setLoading(true);
    setError("");

    fetch(`https://bolls.life/get-chapter/${translation}/${bookId}/${chapter}/`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch");
        return res.json();
      })
      .then((data: Verse[]) => {
        setVerses(data);
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      })
      .catch(() => {
        setError(
          "Could not load this passage. Please try a different translation or check your connection."
        );
      })
      .finally(() => setLoading(false));
  }, [translation, bookId, chapter]);

  async function handleVerseClick(v: Verse) {
    if (!user) {
      setShowLoginNudge(true);
      setTimeout(() => setShowLoginNudge(false), 4000);
      return;
    }
    setSavingVerse(v.verse);
    await toggleHighlight(
      bookId,
      book?.name ?? "",
      chapter,
      v.verse,
      cleanVerseText(v.text),
      accentColor,
      translation
    );
    setSavingVerse(null);
  }

  function handleNoteClick(e: React.MouseEvent, v: Verse) {
    e.stopPropagation();
    navigate(
      `/notes?bookId=${bookId}&bookName=${encodeURIComponent(book?.name ?? "")}&chapter=${chapter}&verse=${v.verse}&translation=${translation}`
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div
          className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: `${accentColor} transparent transparent transparent` }}
        />
        <p className={`text-sm ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>Loading scripture...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-lg ${
            isDark ? "bg-[#1e1e1e]" : "bg-[#f0ebe3]"
          }`}
        >
          <i className="ri-error-warning-line" style={{ color: accentColor }} />
        </div>
        <p className={`text-sm text-center max-w-sm ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
          {error}
        </p>
      </div>
    );
  }

  if (verses.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <div
          className={`w-10 h-10 flex items-center justify-center rounded-lg ${
            isDark ? "bg-[#1e1e1e]" : "bg-[#f0ebe3]"
          }`}
        >
          <i className={`ri-book-open-line ${isDark ? "text-[#444]" : "text-[#ccc]"}`} />
        </div>
        <p className={`text-sm ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
          Select a book and chapter to begin reading.
        </p>
      </div>
    );
  }

  return (
    <div>
      {showLoginNudge && (
        <LoginNudge isDark={isDark} onClose={() => setShowLoginNudge(false)} />
      )}

      {/* Header */}
      <div className={`mb-6 pb-5 border-b ${isDark ? "border-[#1a1a1a]" : "border-[#e8e0d4]"}`}>
        <h2
          className={`text-2xl md:text-3xl font-bold mb-1 ${
            isDark ? "text-white" : "text-[#1a1a1a]"
          }`}
        >
          {book?.name} {chapter}
        </h2>
        <p className={`text-sm ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>{translation}</p>
      </div>

      {/* Verses */}
      <p
        className={`text-base leading-7 tracking-wide ${
          isDark ? "text-[#ccc]" : "text-[#2a1f14]"
        }`}
      >
        {verses.map((v) => {
          const highlighted = isHighlighted(bookId, chapter, v.verse);
          const hlColor = getHighlightColor(bookId, chapter, v.verse);
          const isSaving = savingVerse === v.verse;
          const isHovered = hoveredVerse === v.verse;

          return (
            <span
              key={v.pk}
              className="relative group"
              style={{ display: "inline" }}
            >
              {/* Verse number */}
              <sup
                className="font-bold mr-[2px] select-none text-[0.65em]"
                style={{ color: accentColor }}
              >
                {v.verse}
              </sup>

              {/* Verse text */}
              <span
                onClick={() => handleVerseClick(v)}
                onMouseEnter={() => setHoveredVerse(v.verse)}
                onMouseLeave={() => setHoveredVerse(null)}
                title={highlighted ? "Click to remove highlight" : "Click to highlight"}
                className="cursor-pointer rounded transition-all duration-200"
                style={
                  highlighted && hlColor
                    ? {
                        backgroundColor: hlColor + "33",
                        borderBottom: `2px solid ${hlColor}`,
                        paddingBottom: "1px",
                      }
                    : isHovered
                    ? {
                        backgroundColor: accentColor + "18",
                        borderBottom: `1px dashed ${accentColor}55`,
                        paddingBottom: "1px",
                      }
                    : {}
                }
              >
                {isSaving ? (
                  <i
                    className="ri-loader-4-line animate-spin text-xs mx-1"
                    style={{ color: accentColor }}
                  />
                ) : null}
                {cleanVerseText(v.text)}
              </span>

              {/* Note icon — appears on hover */}
              <button
                onClick={(e) => handleNoteClick(e, v)}
                onMouseEnter={() => setHoveredVerse(v.verse)}
                onMouseLeave={() => setHoveredVerse(null)}
                title="Add a note to this verse"
                className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 cursor-pointer align-middle"
                style={{ color: accentColor + "99" }}
              >
                <i className="ri-edit-2-line text-xs" />
              </button>

              {v.verse < verses.length ? " " : ""}
            </span>
          );
        })}
      </p>
    </div>
  );
}
